import { useState } from 'react';
import { api, ApiRequestError } from '../api.ts';
import { useSession } from '../session.tsx';
import { AvatarPicker } from '../components/AvatarPicker.tsx';
import { Banner, Button, Card, Field } from '../components/ui.tsx';
import { ThemePicker } from '../components/ThemePicker.tsx';

/**
 * Your own settings, after the first time.
 *
 * The setup screen used to be the only place a picture could be chosen, which
 * meant changing it involved being signed out. Same picker, reachable whenever.
 *
 * The handle is shown but not editable: it is what keeps the room legible when
 * two people pick the same display name, and letting anyone take anyone else's
 * is the sort of thing that is funny once. An admin can change it.
 */
export function Profile({ onBack }: { onBack: () => void }) {
  const { session, refresh, theme, changeTheme } = useSession();
  const [displayName, setDisplayName] = useState(session?.user.displayName ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!session) return null;

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await work();
      await refresh();
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-md px-4 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">You</h1>
          {!session.isGuest && (
            <p className="text-sm text-text-muted">@{session.user.username}</p>
          )}
        </div>
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
      </header>

      {error && (
        <div className="mb-4">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}
      {saved && !error && (
        <div className="mb-4">
          <Banner tone="success">Saved.</Banner>
        </div>
      )}

      <Card className="mb-4">
        <AvatarPicker
          value={session.user.avatarValue}
          busy={busy}
          onPickEmoji={(emoji) => void run(() => api.setAvatarEmoji(emoji))}
          onPickFile={(file) => void run(() => api.uploadAvatar(file))}
        />
      </Card>

      <Card className="mb-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(() =>
              api.saveProfile({ displayName, avatarValue: session.user.avatarValue }),
            );
          }}
          className="flex flex-col gap-3"
        >
          <Field
            label="Display name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={24}
            placeholder={session.user.username}
          />
          <Button type="submit" loading={busy} disabled={displayName.trim().length === 0}>
            Save name
          </Button>
        </form>
      </Card>

      <Card>
        <p className="mb-2 text-sm font-medium">Theme</p>
        <ThemePicker theme={theme} onChange={changeTheme} />
      </Card>
    </main>
  );
}
